/**
 * E2E-only control for the onboarding funnel's known-model 1-click add
 * (Bug 3), exposed to Playwright via `globalThis.__irisOnboardingOneClickProbe`.
 *
 * WHY A PROBE. The real known-model 1-click Continue only enables once a live
 * camera preview has connected, and the funnel's connection test only passes
 * once a real decoded frame arrives — both require hardware, so a non-HIL lane
 * cannot press the real button end-to-end. This probe instead drives the REAL
 * onboarding gate through the SAME handoff the fixed source/footer.tsx invokes:
 * it opens the gate at the discovery (Add Video Source) screen, then fires the
 * gate's own `onConfigured` completion with a SEEDED known-model selection
 * (carrying a modelID) and a committed device id. That is exactly the signal the
 * known-model 1-click add was missing — so the spec can assert the funnel leaves
 * the discovery screen and advances into its completion journey, deterministically
 * and without a camera. The gate, its step machine, and its screens stay real.
 *
 * Mirrors `onboarding-auto-link-probe.ts`: same IRIS_E2E_TEST gate, same bind +
 * single global assignment, no product behaviour outside E2E.
 *
 * Per rules: stay-in-scope, think-in-systems, code-comments-required,
 * reference-rules-in-comments (ai.knowledge/global).
 */
import type { OnboardingExit } from '#~/video/components/add/store/types.js';

/** The gate-side surface the probe drives; registered by OnboardingGate. */
export interface OnboardingOneClickControl {
  /** Open the funnel directly on the discovery (Add Video Source) screen. */
  openAtDiscovery(): void;
  /** Fire the gate's known-model completion handoff (source/footer.tsx's signal). */
  completeKnownModelAdd(exit: OnboardingExit): void;
}

interface OnboardingOneClickProbe {
  /** Open the funnel on the discovery screen. */
  open(): void;
  /**
   * Complete a known-model 1-click add against a seeded modelID selection,
   * handing the funnel the committed device id — the fixed footer's signal.
   */
  completeKnownModelAdd(deviceId: string): void;
}

declare global {
  // eslint-disable-next-line no-var
  var __irisOnboardingOneClickProbe: OnboardingOneClickProbe | undefined;
}

// Renderer-process gate. Bare `process.env` is stripped in the client build; the
// real launch env reaches the renderer only through the preload bridge — the
// same read onboarding-auto-link-probe.ts / discovery-mismatch-probe.ts use.
const isE2eEnabled = (): boolean => {
  try {
    return (
      (globalThis as { window?: { electron?: { process?: { env?: Record<string, string> } } } })
        .window?.electron?.process?.env?.IRIS_E2E_TEST === '1'
    );
  } catch {
    return false;
  }
};

let control: OnboardingOneClickControl | undefined;

/** The gate stays real; this only exposes its discovery-open + completion to E2E. */
export const bindOnboardingOneClickControl = (next: OnboardingOneClickControl): (() => void) => {
  if (!isE2eEnabled()) return () => undefined;

  control = next;
  return () => {
    if (control === next) control = undefined;
  };
};

// The seeded known-model discovery row: a modelID-bearing selection is the whole
// point of the known-model path (the fingerprint that lets it skip the config
// screens). No real host is contacted — the committed device id is adopted by
// the connection test, and the probe never needs the camera to stream.
const SEEDED_HOST = 'seeded-known-model.cam.local';
const SEEDED_MODEL_ID = 'seeded-known-model-1';

const buildSeededExit = (deviceId: string): OnboardingExit =>
  ({
    candidate: {
      name: 'Seeded Known-Model Camera',
      modelId: SEEDED_MODEL_ID,
      video: { protocol: 'rtsp', host: SEEDED_HOST, port: 554, path: '/stream' }
    },
    agentId: undefined,
    selection: {
      id: 'seeded-known-model-discovery',
      modelId: SEEDED_MODEL_ID,
      video: { protocol: 'rtsp', host: SEEDED_HOST }
    },
    template: undefined,
    defaultName: 'Seeded Known-Model Camera',
    deviceId
  }) as unknown as OnboardingExit;

if (isE2eEnabled()) {
  const probe: OnboardingOneClickProbe = {
    open(): void {
      if (control === undefined) throw new Error('Onboarding one-click probe is not bound');
      control.openAtDiscovery();
    },
    completeKnownModelAdd(deviceId: string): void {
      if (control === undefined) throw new Error('Onboarding one-click probe is not bound');
      control.completeKnownModelAdd(buildSeededExit(deviceId));
    }
  };

  globalThis.__irisOnboardingOneClickProbe = probe;
}
